import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ITool } from 'src/app/modules/tools/interfaces/tool.interface';
import { ToolsService } from 'src/app/modules/tools/services/tools.service';
import { PopupService } from 'src/app/shared/services/popup/popup.service';
import {
  ReadAloudService,
  VerbalService,
} from 'src/app/modules/read-aloud/services';
import { PopulationService } from 'src/app/shared/services/population/population.service';

@Component({
  selector: 'htc-popup-toolbar-button',
  templateUrl: './popup-toolbar-button.component.html',
  styleUrls: ['./popup-toolbar-button.component.scss'],
  standalone: false,
})
export class PopupToolbarButtonComponent implements ITool, OnInit, OnDestroy {
  @Input() id!: number;
  // If the tool name should be translate prefix with 'Translate_' and it's translation is under the TOOL_BAR key
  @Input() toolName!: string;
  @Input() icon!: string;
  @Input() toggledIcon?: string;
  @Input() testId = 'htc-popup-toolbar-button';
  @Input() isPreK2Reading = false;
  @Input() extraIcon = { icon: '', toggledIcon: '' };
  @Input() iconSize?: 'small' | 'large' | undefined;
  @Input() isDisabled = false;
  @Input() hasPK2Avatar = false; // used to determine if the icon is a PK2 avatar icon
  @Input() shouldShowVOInProgress: boolean | undefined = false; // used to determine if the verbal population is enabled

  path = '/assets/toolbar-icons/';
  isHovering = false;
  isSelected = false;
  sub = new Subscription();
  showVOInProgressAnim = false; // used to show the VO in progress animation and extra icon toggle
  isPaused = false; // track if audio is currently paused
  private isInSequence = false; // tracks whether a relevant audio sequence is actively playing
  private activeAudioId = ''; // tracks the audio ID that this button started, to avoid reacting to other buttons' audio events

  // Computed property to override external disabled state when in toggled state
  get isButtonDisabled(): boolean {
    return this.isDisabled && !this.showVOInProgressAnim && !this.isPaused;
  }

  constructor(
    private toolsService: ToolsService,
    private popupService: PopupService,
    private translateService: TranslateService,
    private readAloudService: ReadAloudService,
    private verbalService: VerbalService,
    private populationService: PopulationService
  ) {}

  ngOnInit(): void {
    if (this.toolName) {
      this.translateName(); //translate for the current language
      this.sub.add(
        this.translateService.onLangChange.subscribe(() => {
          this.translateName(); //translate for subsequent language changes
        })
      );
    }
    this.sub.add(
      this.toolsService.getActivatedTools().subscribe(toolIds => {
        this.toggleButton(toolIds.indexOf(this.id) !== -1);
      })
    );
    this.sub.add(
      this.verbalService.speechStarted$.subscribe(id => {
        if (
          id &&
          this.showVOInProgressAnim &&
          (id.startsWith('question-text') || id.startsWith('hint-text'))
        ) {
          this.activeAudioId = id;
        }
      })
    );
    this.sub.add(
      this.verbalService.sequenceStarted$.subscribe(data => {
        if (data.startsWith('question-text') || data.startsWith('hint-text')) {
          this.isInSequence = true;
          if (this.showVOInProgressAnim) {
            this.activeAudioId = data;
          }
        }
      })
    );
    this.sub.add(
      this.verbalService.speechEnded$.subscribe(data =>
        this.onQuestionPromptEndOrError(data)
      )
    );
    this.sub.add(
      this.verbalService.speechError$.subscribe(({ id }) =>
        this.onQuestionPromptEndOrError(id, false, true)
      )
    );
    this.sub.add(
      this.verbalService.sequenceEnded$.subscribe(data => {
        this.onSequenceEndOrError(data);
      })
    );
    this.sub.add(
      this.verbalService.sequenceError$.subscribe(({ id }) => {
        this.onSequenceEndOrError(id);
      })
    );
  }

  onSequenceEndOrError(data: string): void {
    if (data.startsWith('question-text') || data.startsWith('hint-text')) {
      this.isInSequence = false;
      this.onQuestionPromptEndOrError(data, true);
    }
  }

  onQuestionPromptEndOrError(
    data: string,
    forcePlay?: boolean,
    fromSpeechError?: boolean
  ): void {
    const blockedWaitingForOwnAudioStart =
      this.showVOInProgressAnim &&
      !this.activeAudioId &&
      !forcePlay &&
      !fromSpeechError;
    if (
      forcePlay ||
      (!this.isInSequence &&
        this.isPromptAudioData(data) &&
        !blockedWaitingForOwnAudioStart &&
        // Only respond to events that match the audio this button started
        (!this.activeAudioId || data.startsWith(this.activeAudioId)))
    ) {
      this.showVOInProgressAnim = false;
      this.isPaused = false;
      this.isHovering = false;
      this.activeAudioId = '';
    }
  }

  private isPromptAudioData(data: string): boolean {
    return (
      (data.startsWith('question-text') || data.startsWith('hint-text')) &&
      !this.populationService.hasPhonemeTokenInField(data)
    );
  }

  translateName(): void {
    this.toolName = this.translateService.instant(
      'TOOL_BAR.' + this.toolName.toUpperCase()
    );
  }

  onClick(event: Event): void {
    // Allow clicks when voiceover is in progress (toggled state) or when not disabled
    if (!this.isButtonDisabled) {
      // If voiceover is in progress (toggled state), toggle pause/resume
      if (this.showVOInProgressAnim || this.isPaused) {
        this.verbalService.togglePause();
        this.isPaused = !this.isPaused;
        return;
      }

      // For non-toggled state, stop read aloud service
      this.readAloudService.stopCurrent();
      if (!this.isSelected) {
        this.toolsService.activateTool(this.id);
        this.popupService.setActiveElement(
          (event?.currentTarget ?? null) as HTMLElement | null
        );
        if (this.shouldShowVOInProgress) {
          this.showVOInProgressAnim = true;
        }
      } else {
        this.toolsService.deactivateTool(this.id);
      }
    }
  }

  toggleButton(value: boolean): void {
    this.isSelected = value;
  }

  openTooltip(): void {
    if (!this.isButtonDisabled) this.isHovering = true;
  }

  closeTooltip(): void {
    if (!this.isButtonDisabled) this.isHovering = false;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.showVOInProgressAnim = false; // reset the animation state
    this.isPaused = false; // reset the pause state
    this.isInSequence = false; // reset the sequence tracking state
    this.activeAudioId = ''; // reset the active audio tracking
  }

  getExtraIcon(): string {
    const result =
      this.showVOInProgressAnim && !this.isPaused
        ? this.extraIcon.toggledIcon ?? this.extraIcon.icon
        : this.extraIcon.icon;
    return result;
  }
}
